import { useTranslation } from "react-i18next";
import { Check, ClipboardList, MessageSquare, Code2, Trophy, ArrowRight } from "lucide-react";

export default function About() {
  const { t } = useTranslation("landing");

  const checks = [
    t("overview.features.projects"),
    t("overview.features.sessions"),
    t("overview.features.ratio"),
    t("overview.features.mentorship", { defaultValue: "Personalized Mentorship" }),
    t("overview.features.schedules", { defaultValue: "Flexible Schedules" }),
  ];

  const cards = [
    { icon: ClipboardList, title: t("overview.metrics.sessions.title"), body: t("overview.metrics.sessions.description") },
    { icon: MessageSquare, title: t("overview.metrics.mentorship.title"), body: t("overview.metrics.mentorship.description") },
    { icon: Code2, title: t("overview.metrics.labs.title"), body: t("overview.metrics.labs.description") },
    { icon: Trophy, title: t("overview.metrics.network.title"), body: t("overview.metrics.network.description") },
  ];

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="program" className="scroll-mt-20 lg:scroll-mt-24 bg-soft-grad py-20 overflow-hidden w-full">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* Desktop: 2 columns | Mobile: 1 column */}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">

          {/* LEFT — text column */}
          <div className="text-center lg:text-left">
            <p className="label-eyebrow">{t("overview.tag")}</p>
            <h2 className="mt-3 text-3xl font-bold leading-tight text-navy sm:text-4xl">
              {t("overview.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-body">
              {t("overview.subtitle")}
            </p>

            <ul className="mt-6 space-y-3 text-left">
              {checks.map((c) => (
                <li key={c} className="flex items-start gap-3">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-base font-medium text-navy">{c}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex justify-center lg:justify-start">
              <button
                onClick={() => scrollToSection("themes")}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow border-0 cursor-pointer"
              >
                {t("overview.learnMore", { defaultValue: "Learn More" })}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* RIGHT — metric cards, 1 col mobile → 2 col sm+ */}
          <div className="grid grid-cols-1 gap-8 pt-6 sm:grid-cols-2 sm:gap-6">
            {cards.map((c) => (
              <div
                key={c.title}
                className="group relative rounded-2xl border border-border bg-white p-6 pt-10 shadow-soft transition-all hover:-translate-y-1 hover:shadow-card"
              >
                <span className="absolute -top-6 left-6 grid h-12 w-12 place-items-center rounded-full bg-blue-bg text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <c.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-2 text-lg font-bold text-navy">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-body">{c.body}</p>
              </div>
            ))}
          </div>

        </div>

        {/* Bottom banner */}
        <div className="mt-14 grid grid-cols-1 gap-3 rounded-3xl bg-navy p-8 text-center text-white sm:grid-cols-3 sm:p-10">
          <div className="font-semibold">{t("overview.footerTitle", { defaultValue: "Built for the Future. Backed by Experts." })}</div>
          <div className="text-white/70 sm:border-x sm:border-white/10">{t("overview.footerSub", { defaultValue: "A future-ready curriculum. Real-world learning." })}</div>
          <div className="font-semibold">{t("overview.footerSeats", { defaultValue: "Limited seats. Maximum impact." })}</div>
        </div>

      </div>
    </section>
  );
}
